import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Container,
  Grid,
  Pagination,
  Stack,
} from "@mui/material";
import { useHistory } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { formatDistanceToNow } from "date-fns";

// Constants & Theme
import { Path } from "../../../../static/Constants";
import NavBar from "../../../Components/NavBar";
import { colors, borderRadiusPx } from "../../../../theme";
import { CommonBreadcrumbs } from "../../../Components/ui";

// Subcomponents
import {
  NewsHero,
  NewsFeaturedCard,
  NewsFilters,
  PublicNewsCard,
  NewsArticleModal,
  NewsEmptyState,
  NewsHeroSkeleton,
  NewsGridSkeleton,
} from "./components";

import "./NewsStyle.css";

const stripHtml = (html) => {
  if (!html) return "";
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

const formatTimeAgo = (dateTimeString) => {
  if (!dateTimeString) return "Recently";
  try {
    const date = new Date(dateTimeString);
    if (isNaN(date.getTime())) return "Recently";
    return formatDistanceToNow(date, { addSuffix: true });
  } catch {
    return "Recently";
  }
};

const formatPublishDate = (dateTimeString) => {
  if (!dateTimeString) return "Recently";
  try {
    const date = new Date(dateTimeString);
    if (isNaN(date.getTime())) return "Recently";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "Recently";
  }
};

export const isFeaturedArticle = (item) => {
  if (!item) return false;
  return (
    item.featured === true ||
    item.featured === 1 ||
    item.featured === "1" ||
    item.featured === "true" ||
    item.isFeatured === true ||
    item.isFeatured === 1 ||
    item.isFeatured === "1" ||
    item.isFeatured === "true"
  );
};

const ITEMS_PER_PAGE = 9;

function NewsScreen() {
  const history = useHistory();
  const apiUrl =
    process.env.REACT_APP_API_KEY || "https://testapi.autobetlottery.com/api";
  const [news, setNews] = useState(() => {
    try {
      const saved = localStorage.getItem("news");
      if (saved) {
        const parsed = JSON.parse(saved);
        return Array.isArray(parsed) ? parsed : [];
      }
    } catch {}
    return [];
  });
  const [loading, setLoading] = useState(() => {
    try {
      const saved = localStorage.getItem("news");
      if (saved) {
        const parsed = JSON.parse(saved);
        return !Array.isArray(parsed) || parsed.length === 0;
      }
    } catch {}
    return true;
  });
  const [refreshing, setRefreshing] = useState(false);

  // Filters, Sort, View, Pagination State
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [viewMode, setViewMode] = useState("grid");
  const [page, setPage] = useState(1);

  // Active Reading Modal
  const [selectedArticle, setSelectedArticle] = useState(null);

  const fetchData = useCallback(
    async (isRefresh = false) => {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setNews((current) => {
          if (!current || current.length === 0) {
            setLoading(true);
          }
          return current;
        });
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);

      try {
        const response = await fetch(`${apiUrl}/news/getAllNews`, {
          method: "GET",
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (response.ok) {
          const data = await response.json();
          const items = Array.isArray(data.result)
            ? data.result
            : Array.isArray(data)
            ? data
            : [];
          setNews(items);
          try {
            localStorage.setItem("news", JSON.stringify(items));
          } catch (storageErr) {
            console.warn("[News] LocalStorage write error:", storageErr);
          }
        } else {
          console.error("Error fetching news:", response.statusText);
          const savedNews = localStorage.getItem("news");
          if (savedNews) {
            try {
              setNews(JSON.parse(savedNews));
            } catch (e) {
              console.error("Error parsing localStorage news:", e);
            }
          }
        }
      } catch (error) {
        clearTimeout(timeoutId);
        console.error("Error fetching news:", error);
        const savedNews = localStorage.getItem("news");
        if (savedNews) {
          try {
            setNews(JSON.parse(savedNews));
          } catch (e) {
            console.error("Error parsing localStorage news:", e);
          }
        }
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [apiUrl]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Tab count calculations
  const countsByTab = useMemo(() => {
    const total = news.length;
    const featured = news.filter(isFeaturedArticle).length;
    const announcements = news.filter((item) => {
      const text = `${item.title || ""} ${item.description || ""}`.toLowerCase();
      return (
        text.includes("announc") ||
        text.includes("partner") ||
        text.includes("launch") ||
        text.includes("airdrop") ||
        text.includes("event")
      );
    }).length;
    const updates = news.filter((item) => {
      const text = `${item.title || ""} ${item.description || ""}`.toLowerCase();
      return (
        text.includes("update") ||
        text.includes("release") ||
        text.includes("version") ||
        text.includes("feature") ||
        text.includes("patch") ||
        text.includes("upgrade")
      );
    }).length;

    return {
      all: total,
      featured,
      announcements,
      updates,
    };
  }, [news]);

  // Filtered & Sorted Articles List
  const filteredNews = useMemo(() => {
    let result = [...news];

    // 1. Tab Categorization Filter
    if (activeTab === "featured") {
      result = result.filter(isFeaturedArticle);
    } else if (activeTab === "announcements") {
      result = result.filter((item) => {
        const text = `${item.title || ""} ${item.description || ""}`.toLowerCase();
        return (
          text.includes("announc") ||
          text.includes("partner") ||
          text.includes("launch") ||
          text.includes("airdrop") ||
          text.includes("event")
        );
      });
    } else if (activeTab === "updates") {
      result = result.filter((item) => {
        const text = `${item.title || ""} ${item.description || ""}`.toLowerCase();
        return (
          text.includes("update") ||
          text.includes("release") ||
          text.includes("version") ||
          text.includes("feature") ||
          text.includes("patch") ||
          text.includes("upgrade")
        );
      });
    }

    // 2. Search Query Filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter((item) => {
        const titleMatch = (item.title || "").toLowerCase().includes(q);
        const descMatch = stripHtml(item.description || "")
          .toLowerCase()
          .includes(q);
        return titleMatch || descMatch;
      });
    }

    // 3. Sorting
    result.sort((a, b) => {
      const dateA = new Date(a.updatedOn || a.createdOn || 0).getTime();
      const dateB = new Date(b.updatedOn || b.createdOn || 0).getTime();

      if (sortBy === "newest") {
        return dateB - dateA;
      }
      if (sortBy === "oldest") {
        return dateA - dateB;
      }
      if (sortBy === "title-asc") {
        return (a.title || "").localeCompare(b.title || "");
      }
      if (sortBy === "title-desc") {
        return (b.title || "").localeCompare(a.title || "");
      }
      return 0;
    });

    return result;
  }, [news, activeTab, searchQuery, sortBy]);

  // Featured spotlight article (top article on page 1 of All tab when not searching)
  const isDefaultView = activeTab === "all" && !searchQuery.trim() && page === 1;
  const featuredArticle = filteredNews.find(isFeaturedArticle);
  const spotlightArticle = isDefaultView
    ? (featuredArticle || (filteredNews.length > 0 ? filteredNews[0] : null))
    : null;
  const displayList = spotlightArticle
    ? filteredNews.filter((item) => String(item.id) !== String(spotlightArticle.id))
    : filteredNews;

  // Pagination calculation
  const totalPages = Math.ceil(displayList.length / ITEMS_PER_PAGE);
  const paginatedArticles = useMemo(() => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    return displayList.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [displayList, page]);

  // Latest update relative time string for Hero
  const latestUpdateTime = useMemo(() => {
    if (!news || news.length === 0) return "No updates yet";
    const newest = news.reduce((latest, curr) => {
      const currTime = new Date(curr.updatedOn || curr.createdOn || 0).getTime();
      const latestTime = new Date(latest.updatedOn || latest.createdOn || 0).getTime();
      return currTime > latestTime ? curr : latest;
    }, news[0]);
    return formatTimeAgo(newest.updatedOn || newest.createdOn);
  }, [news]);

  /**
   * Action Handlers
   */
  const handleShare = (article) => {
    if (!article) return;
    const shareUrl = `${window?.location?.origin}${Path.getNewsDetails(article.id)}`;
    try {
      navigator.clipboard.writeText(shareUrl);
      toast.success("Shareable link copied to clipboard!", {
        position: "top-right",
      });
    } catch (err) {
      console.error("Could not copy link:", err);
    }
  };

  const handleRead = (article) => {
    if (!article) return;
    setSelectedArticle(article);
  };

  const handleOpenPage = (article) => {
    if (!article) return;
    history.push(Path.getNewsDetails(article.id));
  };

  const handleResetFilters = () => {
    setSearchQuery("");
    setActiveTab("all");
    setSortBy("newest");
    setPage(1);
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setPage(1);
  };

  const handleSearchChange = (query) => {
    setSearchQuery(query);
    setPage(1);
  };

  return (
    <Box className="news-canvas" sx={{ bgcolor: colors.dashboard.canvas, pb: 8 }}>
      <NavBar />
      <ToastContainer />

      <Container maxWidth="lg" sx={{ pt: { xs: "96px", sm: "108px", md: "116px" }, pb: 4, flexGrow: 1 }}>
        {/* Breadcrumbs Navigation */}
        <CommonBreadcrumbs
          items={[
            { label: "Home", path: Path.root },
            { label: "News & Updates" },
          ]}
        />


        {/* Hero Section */}
        {loading ? (
          <NewsHeroSkeleton />
        ) : (
          <NewsHero
            totalArticlesCount={news.length}
            featuredCount={countsByTab.featured}
            latestUpdateTime={latestUpdateTime}
          />
        )}

        {/* Featured Headline Spotlight (Only on default unfiltered Page 1) */}
        {!loading && spotlightArticle && (
          <NewsFeaturedCard
            article={spotlightArticle}
            onRead={handleRead}
            onShare={handleShare}
            formatTimeAgo={formatTimeAgo}
            formatPublishDate={formatPublishDate}
          />
        )}

        {/* Filters, Search & View Controls */}
        <NewsFilters
          activeTab={activeTab}
          onTabChange={handleTabChange}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          sortBy={sortBy}
          onSortChange={setSortBy}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          countsByTab={countsByTab}
          onRefresh={() => fetchData(true)}
          refreshing={refreshing}
        />

        {/* Articles Content Section */}
        {loading ? (
          <NewsGridSkeleton count={6} viewMode={viewMode} />
        ) : paginatedArticles.length > 0 ? (
          <>
            <Grid container spacing={3}>
              {paginatedArticles.map((article, index) => (
                <Grid
                  item
                  xs={12}
                  sm={viewMode === "grid" ? 6 : 12}
                  md={viewMode === "grid" ? 4 : 12}
                  key={article.id || index}
                >
                  <PublicNewsCard
                    article={article}
                    viewMode={viewMode}
                    onRead={handleRead}
                    onShare={handleShare}
                    formatTimeAgo={formatTimeAgo}
                    formatPublishDate={formatPublishDate}
                  />
                </Grid>
              ))}
            </Grid>

            {/* Pagination */}
            {totalPages > 1 && (
              <Stack
                direction="row"
                justifyContent="center"
                alignItems="center"
                sx={{ mt: 5, mb: 2 }}
              >
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, value) => {
                    setPage(value);
                    window.scrollTo({ top: 320, behavior: "smooth" });
                  }}
                  color="primary"
                  shape="rounded"
                  size="medium"
                  sx={{
                    "& .MuiPaginationItem-root": {
                      fontWeight: 600,
                      borderRadius: borderRadiusPx.md,
                    },
                    "& .Mui-selected": {
                      bgcolor: `${colors.primary.main} !important`,
                      color: `${colors.neutral.white} !important`,
                    },
                  }}
                />
              </Stack>
            )}
          </>
        ) : (
          <NewsEmptyState
            hasSearchQuery={Boolean(searchQuery.trim() || activeTab !== "all")}
            onResetFilters={handleResetFilters}
          />
        )}
      </Container>

      {/* Full Article Reading Modal */}
      <NewsArticleModal
        open={Boolean(selectedArticle)}
        onClose={() => setSelectedArticle(null)}
        article={selectedArticle}
        onShare={handleShare}
        onOpenPage={handleOpenPage}
        formatTimeAgo={formatTimeAgo}
        formatPublishDate={formatPublishDate}
      />
    </Box>
  );
}

export default NewsScreen;
